import moment from 'moment-timezone';

/**
 * ELNKWEB-3246: pure hours-of-operation resolution for the call center.
 *
 * Nothing in this module touches React, Redux or module-level config - every input arrives as
 * an argument. That is deliberate on two counts: it makes the schedule logic unit-testable
 * without mounting a hook, and it is the seam the checkout service plugs into later, when the
 * config stops being a committed constant and starts being a fetched one.
 */

/** ISO-8601 weekday numbering, as produced by the moment `E` token: 1 = Monday .. 7 = Sunday. */
const ISO_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** '07:30' -> 450. Assumes the caller has already validated the shape. */
export const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(':');

  return (Number(h) * 60) + Number(m);
};

const isWellFormedInterval = (interval) =>
  Boolean(interval) &&
  HHMM.test(interval.start) &&
  HHMM.test(interval.end) &&
  toMinutes(interval.start) < toMinutes(interval.end);

/**
 * Collect every structural problem in an hours config. Returns [] when the config is sound.
 *
 * Callers decide what to do with the result: hours.js throws on it outside production, so a
 * malformed schedule cannot reach a build, while resolveCallCenterOpen degrades to "closed"
 * at runtime rather than throwing inside a render.
 */
export const validateHoursConfig = (hours) => {
  const errors = [];

  if (!hours || typeof hours !== 'object') {
    return ['config is not an object'];
  }
  if (!hours.timezone || typeof hours.timezone !== 'string') {
    errors.push('timezone is missing or not a string');
  }

  const checkIntervals = (intervals, label) => {
    if (!Array.isArray(intervals)) {
      errors.push(`${label}: expected an array of intervals (use [] to mean closed)`);

      return;
    }
    intervals.forEach((interval, i) => {
      if (!isWellFormedInterval(interval)) {
        errors.push(
          `${label}[${i}]: expected { start, end } as HH:mm with start < end, got ${JSON.stringify(interval)}`
        );
      }
    });
  };

  if (!hours.schedule || typeof hours.schedule !== 'object') {
    errors.push('schedule is missing');
  } else {
    ISO_DAY_KEYS.forEach((day) => {
      if (!(day in hours.schedule)) {
        errors.push(`schedule.${day} is missing (use [] to mean closed)`);
      } else {
        checkIntervals(hours.schedule[day], `schedule.${day}`);
      }
    });
  }

  if (hours.dateOverrides !== undefined) {
    if (typeof hours.dateOverrides !== 'object' || hours.dateOverrides === null) {
      errors.push('dateOverrides is not an object');
    } else {
      Object.entries(hours.dateOverrides).forEach(([date, intervals]) => {
        if (!ISO_DATE.test(date)) {
          errors.push(`dateOverrides."${date}": expected a YYYY-MM-DD key`);
        }
        checkIntervals(intervals, `dateOverrides."${date}"`);
      });
    }
  }

  return errors;
};

/**
 * Is the call center open at `now`, per `hours`?
 *
 * The date, the weekday and the time-of-day all come out of ONE moment-timezone format call. That is
 * load-bearing, not tidiness: reading the weekday from `now.getDay()` would be the visitor's
 * local weekday, and at 11:00 pm Sunday Pacific it is already Monday in Eastern - a day with
 * different hours. Deriving all three from the same formatted instant makes disagreement
 * structurally impossible.
 *
 * Interval comparison is half-open (start <= t < end), matching the `>= 8 && < 23` convention
 * the single-window implementation used, so "7:30 AM - 11:00 PM" is open through 22:59.
 */
export const resolveCallCenterOpen = (now, hours) => {
  if (!hours || !hours.schedule || !hours.timezone) {
    return false;
  }

  // An invalid Date or an unknown zone must not take a checkout page down. Fail to closed,
  // which routes the customer to the buy flow - the state in which they can still transact.
  // moment does not throw for either (it would fall back to UTC for an unknown zone), so both
  // are checked up front.
  if (!moment.tz.zone(hours.timezone) || !moment(now).isValid()) {
    return false;
  }

  const formatted = moment(now).tz(hours.timezone).format('YYYY-MM-DD HH:mm E');

  const [date, time, isoDay] = formatted.split(' ');
  const dayKey = ISO_DAY_KEYS[Number(isoDay) - 1];

  // A date override wins outright, including when it is [] - an explicitly closed day. `??`
  // is correct here precisely because [] is not nullish, so a closure is honoured rather than
  // falling through to the weekday's hours.
  const intervals = hours.dateOverrides?.[date] ?? hours.schedule[dayKey];

  if (!Array.isArray(intervals)) {
    return false;
  }

  const minutes = toMinutes(time);

  return intervals.some(
    (interval) =>
      isWellFormedInterval(interval) &&
      minutes >= toMinutes(interval.start) &&
      minutes < toMinutes(interval.end)
  );
};
